import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Customer } from '../../types';
import { fetchCustomer, fetchProjectCustomerId, setProjectCustomer } from '../../services/crmService';
import { describeError } from '../../services/errorMessage';
import { rechtFehlerOder, rechtTitel } from '../../services/rechte/rechtFehler';
import { CustomerPickerPanel } from '../crm/CustomerPickerDialog';
import { formatCustomerLabel, resolveLinkAction } from './customerLinkLogic';
import { Briefcase, Search, Loader2, AlertCircle, Link2, Link2Off, X, ChevronDown } from 'lucide-react';

interface Props {
    projectId?: string;
    /**
     * Darf die Verknuepfung setzen/aendern/entfernen — dieselbe Regel wie im
     * Rechnungsmodul: Admin oder Recht `rechnungen.bearbeiten` oder
     * `kunden.verwalten` (Rollensystem). Rechnet der Aufrufer aus.
     */
    darfZuordnen: boolean;
    /** Auftraggeber-Name aus den Projekt-Metadaten (metadata.customerName). */
    contextCustomerName?: string;
    /** Bauherr-Name aus den Projekt-Metadaten (metadata.clientName). */
    contextClientName?: string;
    isReadOnly?: boolean;
}

/**
 * Kunden-Verknuepfung des Projekts (projects.customer_id) im Stammdaten-Bereich.
 *
 * Schreibt ausschliesslich ueber crmService.setProjectCustomer, das nur die
 * Spalte customer_id anfasst — data bleibt unberuehrt, damit ein parallel
 * offener Berichtsstand nicht ueberschrieben wird. Deshalb laeuft hier bewusst
 * KEIN onUpdate(report).
 */
export const ProjectCustomerLink: React.FC<Props> = ({
    projectId, darfZuordnen, contextCustomerName, contextClientName, isReadOnly = false,
}) => {
    const hasProject = Boolean(projectId) && projectId !== 'temp-id';

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [customerId, setCustomerId] = useState<string | null>(null);
    const [customer, setCustomer] = useState<Customer | null>(null);

    const [pickerOpen, setPickerOpen] = useState(false);
    const [pending, setPending] = useState<{ next: Customer | null; action: 'change' | 'remove' } | null>(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const wrapRef = useRef<HTMLDivElement>(null);

    const canEdit = darfZuordnen && hasProject && !isReadOnly;

    const load = useCallback(async () => {
        if (!hasProject) { setLoading(false); return; }
        setLoading(true);
        setLoadError(null);
        try {
            const id = await fetchProjectCustomerId(projectId!);
            setCustomerId(id);
            // Kundendetails koennen an der RLS scheitern (CRM ist admin-nah):
            // dann bleibt die Verknuepfung sichtbar, nur ohne Namen.
            if (id) {
                try { setCustomer(await fetchCustomer(id)); } catch { setCustomer(null); }
            } else {
                setCustomer(null);
            }
        } catch (e) {
            setLoadError(describeError(e));
        } finally {
            setLoading(false);
        }
    }, [projectId, hasProject]);

    useEffect(() => { load(); }, [load]);

    // Klick ausserhalb schliesst das Dropdown (Muster aus crm/CustomerPicker).
    useEffect(() => {
        if (!pickerOpen) return;
        const onDown = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
                setPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [pickerOpen]);

    // Die Kundenliste laedt das Panel selbst, sobald es montiert ist — also
    // genau beim Oeffnen des Dropdowns.
    const openPicker = () => {
        setPickerOpen(true);
        setSaveError(null);
    };

    const apply = async (next: Customer | null) => {
        if (!projectId) return;
        setSaving(true);
        setSaveError(null);
        try {
            await setProjectCustomer(projectId, next ? next.id : null);
            setCustomerId(next ? next.id : null);
            setCustomer(next);
            setPending(null);
            setPickerOpen(false);
        } catch (e) {
            // Abweisung der Datenbank wegen eines fehlenden Rechts im Klartext.
            setSaveError(rechtFehlerOder(e));
        } finally {
            setSaving(false);
        }
    };

    const choose = (c: Customer) => {
        const action = resolveLinkAction(customerId, c.id);
        if (action === 'noop') { setPickerOpen(false); return; }
        // Wechsel bestaetigen: Rechnungen und Kundenakte haengen an dieser Verknuepfung.
        if (action === 'change') { setPending({ next: c, action: 'change' }); return; }
        apply(c);
    };

    const requestRemove = () => setPending({ next: null, action: 'remove' });

    if (!hasProject) return null;

    const linkedLabel = customer
        ? formatCustomerLabel(customer)
        : customerId
            ? 'Verknüpft (Kundendaten nicht sichtbar)'
            : '';

    return (
        <div ref={wrapRef} className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-inner relative">
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-blue-600" /> Kunde (CRM)
                </h4>
                {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            </div>

            {loadError ? (
                <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg p-2">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>Kunden-Verknüpfung konnte nicht geladen werden: {loadError}</span>
                </div>
            ) : (
                <>
                    <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-sm ${customerId ? 'bg-white border-slate-200' : 'bg-white border-dashed border-slate-300'}`}>
                        {customerId
                            ? <Link2 className="w-4 h-4 text-emerald-600 shrink-0" />
                            : <Link2Off className="w-4 h-4 text-slate-300 shrink-0" />}
                        <span className={`flex-1 truncate ${customerId ? 'font-bold text-slate-800' : 'text-slate-400 italic'}`}>
                            {loading ? 'Wird geladen…' : (customerId ? linkedLabel : 'Kein Kunde verknüpft')}
                        </span>
                    </div>

                    {!darfZuordnen && (
                        <p className="text-[11px] text-slate-400 mt-2">
                            Die Kunden-Zuordnung pflegt, wer das Recht „{rechtTitel('kunden.verwalten')}“ oder „{rechtTitel('rechnungen.bearbeiten')}“ hat.
                        </p>
                    )}

                    {canEdit && (
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                            <button
                                type="button"
                                onClick={openPicker}
                                disabled={saving}
                                className="text-[11px] font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-100 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
                            >
                                <Search className="w-3.5 h-3.5" /> {customerId ? 'Kunde ändern' : 'Kunde zuweisen'}
                                <ChevronDown className="w-3 h-3" />
                            </button>
                            {customerId && (
                                <button
                                    type="button"
                                    onClick={requestRemove}
                                    disabled={saving}
                                    className="text-[11px] font-bold text-slate-600 bg-white hover:bg-slate-100 border border-slate-200 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                >
                                    <Link2Off className="w-3.5 h-3.5" /> Verknüpfung entfernen
                                </button>
                            )}
                            {saving && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
                        </div>
                    )}

                    {saveError && (
                        <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg p-2 mt-2">
                            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>{saveError}</span>
                        </div>
                    )}
                </>
            )}

            {/* ─── Auswahl-Dropdown ─── */}
            {pickerOpen && canEdit && (
                <div className="absolute z-50 left-4 right-4 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-fade-in">
                    <CustomerPickerPanel
                        onPick={choose}
                        contextCustomerName={contextCustomerName}
                        contextClientName={contextClientName}
                        currentCustomerId={customerId}
                        disabled={saving}
                    />
                </div>
            )}

            {/* ─── Bestaetigung bei Wechsel/Entfernen ─── */}
            {pending && (
                <div className="fixed inset-0 z-[100] bg-slate-900/40 flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden">
                        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
                                <AlertCircle className="w-4 h-4 text-amber-500" />
                                {pending.action === 'remove' ? 'Verknüpfung entfernen?' : 'Kunde wirklich ändern?'}
                            </h3>
                            <button type="button" onClick={() => setPending(null)} className="text-slate-400 hover:text-slate-600">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="px-5 py-4 space-y-3 text-sm text-slate-600">
                            <p>
                                Bisher verknüpft: <strong className="text-slate-800">{linkedLabel || '—'}</strong>
                            </p>
                            {pending.action === 'change' && (
                                <p>
                                    Neu: <strong className="text-slate-800">{formatCustomerLabel(pending.next)}</strong>
                                </p>
                            )}
                            <p className="text-xs bg-amber-50 border border-amber-100 text-amber-800 rounded-lg p-3">
                                Rechnungen und die Kundenakte laufen über diese Verknüpfung. Bereits erstellte Rechnungen
                                bleiben unverändert, künftige Auswertungen ordnen dieses Projekt aber dem
                                {pending.action === 'remove' ? ' bisherigen Kunden nicht mehr zu.' : ' neuen Kunden zu.'}
                            </p>
                            {saveError && <p className="text-xs text-red-600">{saveError}</p>}
                        </div>
                        <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setPending(null)}
                                disabled={saving}
                                className="px-4 py-2 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50"
                            >
                                Abbrechen
                            </button>
                            <button
                                type="button"
                                onClick={() => apply(pending.next)}
                                disabled={saving}
                                className="px-4 py-2 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                {pending.action === 'remove' ? 'Entfernen' : 'Ändern'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
